import { useCallback, useState } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormHelperText,
  FormErrorMessage,
  type FormControlProps,
  Tag,
  Box,
} from '@chakra-ui/react';

import { testIds } from './testIds';

export interface TextFieldProps extends Omit<FormControlProps, 'onChange' | 'onBlur'> {
  id: string;
  label: string;
  isMultiline?: boolean;
  value: string;
  onChange?: (value: string) => void;
  onBlur: (value: string) => void;
  placeholder?: string;
  helperText?: string;
  error?: string;
  isCorrect?: boolean | null;
}

/**
 * A TextField component that supports both single-line input and textarea
 */
export function TextField({
  id,
  label,
  isMultiline = false,
  value,
  onChange,
  onBlur,
  placeholder,
  helperText,
  error,
  isCorrect,
  ...formControlProps
}: TextFieldProps) {
  const isInvalid = error !== undefined && error.length > 0;
  const [localValue, setLocalValue] = useState(value);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (onChange !== undefined) {
        onChange(newValue);
      }
    },
    [setLocalValue, onChange],
  );

  const handleBlur = useCallback(() => {
    onBlur(localValue);
  }, [localValue, onBlur]);

  const InputComponent = isMultiline ? Textarea : Input;

  return (
    <FormControl isInvalid={isInvalid} {...formControlProps} sx={{ display: 'contents' }}>
      <FormLabel htmlFor={id} fontWeight="semibold">
        {typeof isCorrect === 'boolean' && (
          <Tag size="sm" variant="subtle" colorScheme={isCorrect ? 'green' : 'red'} mr={2}>
            {isCorrect ? 'Correct' : 'Incorrect'}
          </Tag>
        )}
        {label}
      </FormLabel>

      <Box gridRow="2" minH={0}>
        {helperText !== undefined && helperText.length > 0 ? (
          <FormHelperText mt={0} mb={1}>
            {helperText}
          </FormHelperText>
        ) : null}
      </Box>

      <Box gridRow="3" display="flex" alignItems={isMultiline ? 'stretch' : 'center'} mt={1}>
        <InputComponent
          id={id}
          data-testid={`${testIds.textField}-${id}`}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          _placeholder={{ color: 'fg.disabled' }}
          isDisabled={
            (formControlProps.isDisabled ?? false) || (formControlProps.isReadOnly ?? false)
          }
          _disabled={{ color: 'fg.muted', cursor: 'not-allowed' }}
          width="100%"
        />
      </Box>

      <Box gridRow="4" minH={isInvalid ? 'auto' : 0}>
        {isInvalid ? (
          <FormErrorMessage mt={2} mb={0}>
            {error}
          </FormErrorMessage>
        ) : null}
      </Box>
    </FormControl>
  );
}
